// Critical-fields contract — see aegle-docs critical_fields_contract.md
import { Box, Collapse, Typography } from "@mui/material";
import { ErrorOutline, ExpandLess, ExpandMore, WarningAmber } from "@mui/icons-material";
import { useEffect, useState } from "react";

import { CriticalFieldEntry, CriticalFieldSeverity } from "../document-review/criticalFields";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";

interface CriticalFieldGapListProps {
  /** Unmapped critical slots for the current schema, conditional entries already filtered. */
  entries: CriticalFieldEntry[];
}

const SeverityIcon = ({ severity }: { severity: CriticalFieldSeverity }) =>
  severity === "required" ? (
    <ErrorOutline sx={{ fontSize: 16, color: "error.main", mt: "2px" }} />
  ) : (
    <WarningAmber sx={{ fontSize: 16, color: "warning.main", mt: "2px" }} />
  );

/**
 * Lists unmapped critical slots for the current dialog tab. Shrinks as the
 * reviewer maps fields and disappears when the schema's slots are fully
 * covered. Collapsible so the table doesn't get pushed out of view on
 * advisory-heavy schemas; defaults to expanded so reviewers see gaps without
 * an extra click.
 */
export const CriticalFieldGapList = ({ entries }: CriticalFieldGapListProps) => {
  const [expanded, setExpanded] = useState(true);

  // Re-expand when transitioning from "no gaps" → "has gaps" (e.g. switching
  // tabs or undoing a mapping). Without this, a reviewer who collapsed the
  // section on one tab would land on a fresh tab with the section still
  // collapsed, missing the gap signal.
  useEffect(() => {
    if (entries.length > 0) setExpanded(true);
  }, [entries.length]);

  if (entries.length === 0) return null;

  return (
    <Box
      sx={(theme) => ({
        mb: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        backgroundColor: theme.palette.mode === "dark" ? theme.palette.action.hover : theme.palette.background.default,
      })}
    >
      <Box
        component="button"
        type="button"
        aria-expanded={expanded}
        aria-label={expanded ? "Collapse unmapped critical fields" : "Expand unmapped critical fields"}
        onClick={() => setExpanded((v) => !v)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          py: 0.75,
          width: "100%",
          background: "none",
          border: "none",
          textAlign: "left",
          cursor: "pointer",
          color: "inherit",
          font: "inherit",
        }}
      >
        <Typography variant="caption" sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
          Unmapped critical fields ({entries.length})
        </Typography>
        {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, px: 1.5, pb: 1.25, pt: 0.25 }}>
          {entries.map((entry, idx) => (
            <Box key={`${entry.field}-${idx}`} sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
              <SeverityIcon severity={entry.severity} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                  {formatToTitleCase(entry.field)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                  {entry.impact}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};
